"use client";

import { CERTIFICATIONS, PARTNER_GROUPS, PARTNERS_INTRO } from "@/data/about-content";
import { useContent } from "@/hooks/useContent";
import type { Bilingual, Locale } from "@/types/home";
import { motion } from "framer-motion";
import { BadgeCheck } from "lucide-react";
import { useLocale } from "next-intl";

interface AboutContent {
  ecosystem: { eyebrow: Bilingual; title: Bilingual };
  partnerGroups: { id: string; category: Bilingual; names: string[] }[];
  certifications: { id: string; label: Bilingual }[];
}

/** PARTNERS — the ecosystem of consultants + certifications strip (panel-managed). */
export default function AboutPartners() {
  const locale = useLocale() as Locale;
  const { data } = useContent<AboutContent>("about");
  const intro = data?.ecosystem ?? PARTNERS_INTRO;
  const groups = data?.partnerGroups ?? PARTNER_GROUPS.map((g, i) => ({ id: `g${i}`, ...g }));
  const certs = data?.certifications ?? CERTIFICATIONS.map((label, i) => ({ id: `c${i}`, label }));

  if (groups.length === 0 && certs.length === 0) return null;

  return (
    <section id="partners" className="scroll-mt-44 bg-black/[0.02] dark:bg-white/[0.02]">
      <div className="mx-auto max-w-360 px-6 py-20 md:py-28 lg:px-10">
        <span className="text-[11px] uppercase tracking-[0.22em] text-accent">{intro.eyebrow[locale]}</span>
        <h2 className="mt-3 font-bebas text-[clamp(2rem,4vw,3.4rem)] uppercase leading-none text-ash dark:text-white">
          {intro.title[locale]}
        </h2>

        <div className="mt-14 grid gap-px overflow-hidden border border-black/10 bg-black/10 md:grid-cols-3 dark:border-white/10 dark:bg-white/10">
          {groups.map((g, i) => (
            <motion.div
              key={g.id ?? i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white p-8 dark:bg-ash"
            >
              <div className="text-[11px] uppercase tracking-[0.22em] text-accent">{g.category[locale]}</div>
              <ul className="mt-6 space-y-4">
                {g.names.map((n) => (
                  <li key={n} className="font-bebas text-2xl uppercase tracking-wide text-ash/80 transition-colors hover:text-ash dark:text-white/70 dark:hover:text-white">
                    {n}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Certifications */}
        {certs.length > 0 && (
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {certs.map((c, i) => (
              <div key={c.id ?? i} className="flex items-start gap-3 border border-black/10 p-5 dark:border-white/10">
                <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                <span className="text-sm leading-snug text-ash/70 dark:text-white/70">{c.label[locale]}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
